/**
 * DashboardHome — Dritte Portal-Landing-Ansicht neben Picker (/apps) und
 * Tab-Uebersicht (/uebersicht/heute). Welle P3, Joerg 2026-07-06.
 *
 * Aufbau (Anhang A.4-A.6 aus dem Dashboard-Home-mit-Sidebar-Plan):
 * - Rollenspezifische Fokus-Karte oben (Titel + Metrik + Sekundaer-Info + CTAs)
 * - Kachel-Grid darunter (12 Kacheln fuer Handwerker, bis 18 fuer VBGA)
 *
 * Datenquelle Fokus-Karte: /api/portal/dashboard/focus-card
 * Datenquelle Kacheln: /api/portal/my-access (RBAC-Filter)
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock, Hammer, Mic, MessageSquare, Library, Truck, LayoutGrid,
  BarChart3, IdCard, ClipboardList, Text, Users as UsersIcon, Calendar,
  CheckSquare, Wrench, Sparkles, User as UserIcon, Settings,
  Loader2,
} from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';

import { HomeCard, type HomeCardData } from '../components/HomeCard';
import { useSidebarBadges } from '../lib/sidebar-badge-stream';

type Role = 'handwerker' | 'vorarbeiter' | 'buero' | 'gf' | 'admin';

interface FocusCardData {
  role: Role;
  title: string;
  metric: string;
  info: string;
  ctas: Array<{ label: string; href: string }>;
}

interface AccessResponse {
  departments: Array<{ modules: Array<{ slug: string }> }>;
}

interface TileDef {
  slug: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  href: string;
  sub: string;
  requiresModule?: string;
  roles?: Role[];
  iconClassName?: string;
  /** Live-Zaehler aus dem Sidebar-Badge-Stream — KEINE statischen Zahlen
   *  (TB-01, 2026-08-21: Mockup-Hardcodes wie badge:7 zeigten Fantasie-Werte). */
  badgeKey?: 'todos' | 'chat' | 'baustelle';
}

// Anhang A.5 (Handwerker) + A.6 (Leitung) — Reihenfolge + Sub-Info 1:1 aus Mockup
const TILES_HANDWERKER: TileDef[] = [
  { slug: 'zeiterfassung', label: 'Zeiterfassung', icon: Clock,        href: '/m/zeiterfassung',  sub: 'Start · Stop · Anträge',   requiresModule: 'zeiterfassung' },
  { slug: 'bauberichteV2', label: 'Baustelle',     icon: Hammer,       href: '/m/bauberichteV2',  sub: 'Aufgaben + Berichte',       requiresModule: 'bauberichteV2', badgeKey: 'baustelle' },
  { slug: 'onvi-capture',  label: 'Notiz',         icon: Mic,          href: '/m/onvi-capture',   sub: 'Sprache, Foto, GPS',        requiresModule: 'onvi-capture' },
  { slug: 'chat',          label: 'Chat',          icon: MessageSquare,href: '/m/chat',           sub: 'Team',                       requiresModule: 'chat',           badgeKey: 'chat' },
  { slug: 'bibliothek',    label: 'Bibliothek',    icon: Library,      href: '/m/bibliothek',     sub: 'Allgemein + Aufgabe',       requiresModule: 'bibliothek' },
  { slug: 'assistant',     label: 'Assistent',     icon: Sparkles,     href: '/m/assistant',      sub: 'Frag onvi',                  requiresModule: 'assistant' },
  { slug: 'fuhrpark',      label: 'Fuhrpark',      icon: Truck,        href: '/m/fuhrpark',       sub: 'Mein Fahrzeug',              requiresModule: 'fuhrpark' },
  // Jörg-Entscheid 2026-09-06 (A4-Folge): /admin/adressbuch ist fuer reine
  // Handwerker seit dem my-access-Gate eine Verbotsseite — die Kachel zeigt
  // auf die Einstellungen (Passwort, 2FA, Push). Echte "Mein Profil"-Ansicht
  // (eigener Adressbuch-Eintrag, selbst pflegbar) = Backlog nach der Welle.
  { slug: 'mein-profil',   label: 'Mein Profil',   icon: UserIcon,     href: '/settings',         sub: 'Passwort · 2FA · Push' },
  { slug: 'kalender',      label: 'Kalender',      icon: Calendar,     href: '/kalender',         sub: 'Termine + Aufgaben' },
  { slug: 'todos',         label: 'Todos',         icon: CheckSquare,  href: '/todos',            sub: 'Offene Aufgaben',            badgeKey: 'todos' },
  { slug: 'ressourcen',    label: 'Ressourcen',    icon: Wrench,       href: '/m/ressourcen',     sub: 'Ausleihe · QR',              requiresModule: 'ressourcen' },
  { slug: 'glossar',       label: 'Mein Glossar',  icon: Text,         href: '/m/bauberichteV2/glossar', sub: 'Sprach-Korrekturen', requiresModule: 'bauberichteV2' },
];

const TILES_LEITUNG: TileDef[] = [
  { slug: 'zeiterfassung', label: 'Zeiterfassung', icon: Clock,        href: '/m/zeiterfassung',  sub: 'Team + Freigaben',           requiresModule: 'zeiterfassung' },
  { slug: 'bauberichteV2', label: 'Baustelle',     icon: Hammer,       href: '/m/bauberichteV2',  sub: 'Aufgaben + Berichte',        requiresModule: 'bauberichteV2', badgeKey: 'baustelle' },
  { slug: 'onvi-capture',  label: 'Notiz',         icon: Mic,          href: '/m/onvi-capture',   sub: 'Team-Notizen',               requiresModule: 'onvi-capture' },
  { slug: 'chat',          label: 'Chat',          icon: MessageSquare,href: '/m/chat',           sub: 'Team + Kunden',              requiresModule: 'chat',           badgeKey: 'chat' },
  { slug: 'bibliothek',    label: 'Bibliothek',    icon: Library,      href: '/m/bibliothek',     sub: 'Firmen-Dokumente',           requiresModule: 'bibliothek' },
  { slug: 'assistant',     label: 'Assistent',     icon: Sparkles,     href: '/m/assistant',      sub: 'Auswertungen fragen',        requiresModule: 'assistant' },
  { slug: 'fuhrpark',      label: 'Fuhrpark',      icon: Truck,        href: '/m/fuhrpark',       sub: 'Fahrzeuge + Buchungen',       requiresModule: 'fuhrpark' },
  { slug: 'plantafel',     label: 'Plantafel',     icon: LayoutGrid,   href: '/m/plantafel',      sub: 'Wochenübersicht',            requiresModule: 'plantafel' },
  { slug: 'auswertungen',  label: 'Auswertungen',  icon: BarChart3,    href: '/m/disposition',   sub: 'Zeit, Umsatz, Material',     requiresModule: 'disposition' },
  { slug: 'stammdaten',    label: 'Stammdaten',    icon: IdCard,       href: '/m/stammdaten',     sub: 'Material + DATANORM',        requiresModule: 'stammdaten' },
  { slug: 'vorlagen',      label: 'Vorlagen',      icon: ClipboardList,href: '/m/vorlagen',       sub: 'Angebote, Briefe',           requiresModule: 'vorlagen' },
  // 2026-09-06 (A4): requiresModule wie bei allen Modul-Kacheln — ohne Adressbuch-
  // ACL fuehrte die Kachel auf eine leere Seite mit 403-Konsolenrauschen.
  { slug: 'adressbuch',    label: 'Adressbuch',    icon: UsersIcon,    href: '/admin/adressbuch', sub: 'Personen · Gruppen · Kontakte', requiresModule: 'adressbuch' },
  { slug: 'kalender',      label: 'Kalender',      icon: Calendar,     href: '/kalender',         sub: 'Termine + Team' },
  { slug: 'todos',         label: 'Todos',         icon: CheckSquare,  href: '/todos',            sub: 'Offene Aufgaben',             badgeKey: 'todos' },
  { slug: 'ressourcen',    label: 'Ressourcen',    icon: Wrench,       href: '/m/ressourcen',     sub: 'Katalog · Reservierung',     requiresModule: 'ressourcen' },
  { slug: 'glossar',       label: 'Mein Glossar',  icon: Text,         href: '/m/bauberichteV2/glossar',       sub: 'Persönliche Sprach-Korrekturen', requiresModule: 'bauberichteV2' },
  { slug: 'firmen-glossar',label: 'Unternehmensglossar', icon: Text,   href: '/m/bauberichteV2/admin/glossar', sub: 'Firmenweite Korrekturen', roles: ['buero','gf','admin'], requiresModule: 'bauberichteV2', iconClassName: 'text-accent' },
  { slug: 'verwaltung',    label: 'Verwaltung',    icon: Settings,     href: '/admin',            sub: 'Benutzer + System',           roles: ['admin'] },
];

function normalizeRole(raw: string | undefined): Role {
  if (raw === 'admin' || raw === 'gf') return raw;
  if (raw === 'buero') return 'buero';
  if (raw === 'vorarbeiter') return 'vorarbeiter';
  return 'handwerker';
}

export default function DashboardHome() {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  const isHandwerker = role === 'handwerker';
  const isLeitung = !isHandwerker;

  const badges = useSidebarBadges();
  const [focusCard, setFocusCard] = useState<FocusCardData | null>(null);
  const [allowedModules, setAllowedModules] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      api.get<{ success: boolean; data: FocusCardData }>('/portal/dashboard/focus-card')
        .then((r) => setFocusCard(r.data))
        .catch(() => setFocusCard(null)),
      api.get<{ success: boolean; data: AccessResponse }>('/portal/my-access')
        .then((r) => {
          const slugs = new Set<string>();
          for (const dept of r.data?.departments ?? []) {
            for (const mod of dept.modules ?? []) slugs.add(mod.slug);
          }
          setAllowedModules(slugs);
        })
        .catch(() => setAllowedModules(new Set())),
    ]).finally(() => setLoading(false));
  }, []);

  const tiles = useMemo(() => {
    const source = isHandwerker ? TILES_HANDWERKER : TILES_LEITUNG;
    return source.filter((t) => {
      if (t.requiresModule && !allowedModules.has(t.requiresModule)) return false;
      if (t.roles && !t.roles.includes(role)) return false;
      return true;
    });
  }, [isHandwerker, role, allowedModules]);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
      {loading && !focusCard && (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 size={14} className="animate-spin" /> Lädt…
        </div>
      )}
      {focusCard && <FocusCard data={focusCard} />}
      {!focusCard && !loading && <FocusCardFallback role={role} />}

      {/* Mein Tag — rollenspezifische Karten aus /portal/home/today. Nutzt das
          bewaehrte HOME_CARDS-System aus der klassischen Uebersicht. */}
      <MeinTag />

      <div>
        <h2 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Anwendungen</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {tiles.map((t) => {
            const Icon = t.icon;
            const badgeVal = t.badgeKey ? badges[t.badgeKey] : 0;
            return (
              <Link
                key={t.slug}
                to={t.href}
                className="relative rounded-xl border border-default bg-card/60 hover:border-primary/50 hover:bg-card p-4 transition-colors group"
              >
                <div className="flex flex-col gap-1">
                  <Icon size={24} className={`${t.iconClassName ?? 'text-brand-400'} group-hover:scale-110 transition-transform`} />
                  <div className="text-sm font-semibold text-foreground mt-1">{t.label}</div>
                  <div className="text-[11px] text-muted-foreground line-clamp-1">{t.sub}</div>
                </div>
                {badgeVal > 0 && (
                  <span className="absolute top-2 right-2 min-w-[20px] h-5 px-1.5 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center">
                    {badgeVal}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FocusCard({ data }: { data: FocusCardData }) {
  return (
    <div className="rounded-xl border border-primary/40 bg-primary/5 p-5">
      <h1 className="text-lg font-bold text-slate-100">{data.title}</h1>
      <div className="text-4xl font-mono text-primary mt-2 tracking-tight">{data.metric}</div>
      <div className="text-sm text-slate-400 mt-1">{data.info}</div>
      <div className="flex flex-wrap gap-2 mt-4">
        {data.ctas.map((cta, i) => (
          <Link
            key={i}
            to={cta.href}
            className="px-3 py-2 rounded-md bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm font-medium transition-colors"
          >
            {cta.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

function FocusCardFallback({ role }: { role: Role }) {
  const config: Record<Role, FocusCardData> = {
    handwerker: {
      role: 'handwerker', title: '⏱️ Timer läuft', metric: '00:00:00',
      info: 'Kein aktueller Timer.',
      ctas: [{ label: 'Bericht erfassen', href: '/m/bauberichteV2' }],
    },
    vorarbeiter: {
      role: 'vorarbeiter', title: '👷 Deine Kolonne heute', metric: '– / –',
      info: 'Live-Daten kommen aus Plantafel.',
      ctas: [{ label: 'Plantafel', href: '/m/plantafel' }, { label: 'Berichte', href: '/m/bauberichteV2' }],
    },
    buero: {
      role: 'buero', title: '📋 Freigaben heute', metric: '–',
      info: 'Live-Daten kommen aus Bauberichte.',
      ctas: [{ label: 'Berichte', href: '/m/bauberichteV2' }, { label: 'Anträge', href: '/m/zeiterfassung' }],
    },
    gf: {
      role: 'gf', title: '📊 Firma heute', metric: '– / –',
      info: 'Live-Daten kommen aus Auswertungen.',
      ctas: [{ label: 'Auswertungen', href: '/m/disposition' }, { label: 'Plantafel', href: '/m/plantafel' }],
    },
    admin: {
      role: 'admin', title: '🔧 System heute', metric: '0',
      info: 'Warnungen · Alle Container up · Backup ok',
      ctas: [{ label: 'Einstellungen', href: '/settings' }, { label: 'Benutzer', href: '/admin/adressbuch' }],
    },
  };
  return <FocusCard data={config[role]} />;
}

// ============================================================
// Mein Tag — rollenspezifische To-do-Zone (2026-07-06, Weg A)
// Konsumiert den bestehenden /portal/home/today-Endpoint. Karten sind
// rollen-gefiltert (visibility.slug); GF sieht z.B. „Offene Genehmigungen",
// Handwerker „Meine aktiven Aufgaben".
// ============================================================
function MeinTag() {
  const [cards, setCards] = useState<HomeCardData[] | null>(null);

  useEffect(() => {
    api.get<{ success: boolean; data: { cards: HomeCardData[] } }>('/portal/home/today')
      .then((r) => setCards(r.data?.cards ?? []))
      .catch(() => setCards([]));
  }, []);

  if (cards === null) {
    return (
      <div className="rounded-lg border border-default bg-card/40 p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 size={14} className="animate-spin" /> Mein Tag lädt…
      </div>
    );
  }
  if (cards.length === 0) return null;

  return (
    <div>
      <h2 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Mein Tag</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 auto-rows-[minmax(200px,auto)]">
        {cards.map((card) => (
          <HomeCard key={card.id} card={card} />
        ))}
      </div>
    </div>
  );
}
